import React, { useState } from 'react';
import { useListEmailTemplatesQuery } from '@/redux/email-templates/emailTemplatesApi';
import type { CatalogueEntry } from '@/redux/email-templates/emailTemplatesApi';
import PageShell from '@/components/Layout/PageShell';
import { PageHeader } from '@/components/UI/PageHeader';
import { Badge } from '@/components/UI/Badge';
import { Button } from '@/components/UI/Button';
import { Alert } from '@/components/UI/Alert';
import { LoadingState } from '@/components/UI/LoadingState';
import TemplateEditorModal from '@/components/EmailTemplates/TemplateEditorModal';

const EmailTemplatesPage: React.FC = () => {
  const { data, isLoading, isError } = useListEmailTemplatesQuery();
  const [editing, setEditing] = useState<CatalogueEntry | null>(null);

  const templates = (data?.data ?? []).filter((t) => t.key !== 'email_template');

  return (
    <PageShell
      header={
        <PageHeader
          title="Email Templates"
          subtitle="Manage subject lines and HTML content for all system emails. Changes take effect immediately."
        />
      }
    >
      {isLoading && <LoadingState label="Loading templates…" />}

      {isError && (
        <Alert tone="danger" className="p-6 text-sm">
          Failed to load email templates.
        </Alert>
      )}

      {!isLoading && !isError && (
        <div className="table-card">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <th className="th-cell">Template</th>
                  <th className="th-cell">Subject</th>
                  <th className="th-cell">Variables</th>
                  <th className="th-cell" />
                </tr>
              </thead>
              <tbody>
                {templates.map((tpl) => {
                  const hasTenantOverride = Object.keys(tpl.tenant_value ?? {}).length > 0;
                  const subject = String(tpl.effective_value?.subject ?? '');
                  const variables: string[] = tpl.variables ?? [];

                  return (
                    <tr key={tpl.key} className="transition-colors duration-150 hover:bg-ui-hover">
                      <td className="td-cell">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-ui-text">{tpl.name ?? tpl.key}</p>
                          {hasTenantOverride && <Badge variant="warning">tenant override</Badge>}
                        </div>
                        <p className="mt-0.5 text-xs text-ui-text3">{tpl.description}</p>
                        <code className="text-xs text-ui-text3">{tpl.key}</code>
                      </td>
                      <td className="td-cell">
                        <p className="max-w-xs truncate text-sm text-ui-text2">
                          {subject || <span className="italic text-ui-text3">wrapper template</span>}
                        </p>
                      </td>
                      <td className="td-cell">
                        <div className="flex flex-wrap gap-1">
                          {variables.map((v) => (
                            <code key={v} className="rounded-chip bg-accent-soft px-1.5 py-0.5 text-xs text-accent-text">
                              {`{{${v}}}`}
                            </code>
                          ))}
                        </div>
                      </td>
                      <td className="td-cell text-right">
                        <Button variant="link" size="xs" className="text-accent-text" onClick={() => setEditing(tpl)}>
                          Edit
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing && <TemplateEditorModal entry={editing} onClose={() => setEditing(null)} />}
    </PageShell>
  );
};

export default EmailTemplatesPage;
